import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import { ShoppingCart, Check } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { Spinner } from "../components/common/Spinner";
import styles from "./ColorPalettesPage.module.css";

interface ColorPalette {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  inStock: boolean;
}

export function ColorPalettesPage() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [palettes, setPalettes] = useState<ColorPalette[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [addedToCart, setAddedToCart] = useState<string | null>(null);

  useEffect(() => {
    const fetchPalettes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/color-palettes/`);
        const data = await response.json();

        const mappedPalettes: ColorPalette[] = data.palettes.map((palette: any) => {
          let coverImage = "https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=400&fit=crop";
          if (palette.images && palette.images.length > 0) {
            const defaultImg = palette.images.find((img: any) => img.isDefault) || palette.images[0];
            coverImage = defaultImg.url.startsWith("http")
              ? defaultImg.url
              : `${API_BASE_URL}${defaultImg.url}`;
          } else if (palette.imageUrl) {
            coverImage = palette.imageUrl.startsWith("http")
              ? palette.imageUrl
              : `${API_BASE_URL}${palette.imageUrl}`;
          }

          return {
            id: palette._id,
            name: palette.name,
            description: palette.description,
            price: palette.price,
            image: coverImage,
            inStock: palette.inStock,
          };
        });

        setPalettes(mappedPalettes);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching color palettes:", error);
        setLoading(false);
      }
    };

    fetchPalettes();
  }, []);

  return (
    <div className={styles.palettesPage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Color Palettes</h1>
          <p className={styles.heroSubtitle}>
            Pre-mixed acrylic paint sets, sold on their own — perfect for restocking a
            kit or starting a project of your own
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className={styles.mainContent}>
        <div className={styles.container}>
          {loading ? (
            <Spinner label="Loading color palettes..." size="lg" />
          ) : (
            <div className={styles.productsGrid}>
              {palettes.map((palette) => (
                <div
                  key={palette.id}
                  className={styles.productCard}
                  onClick={() => navigate(`/products/palettes/${palette.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  {!palette.inStock && (
                    <div className={styles.outOfStock}>Out of Stock</div>
                  )}
                  <div className={styles.imageContainer}>
                    <img
                      src={palette.image}
                      alt={palette.name}
                      className={styles.productImage}
                    />
                  </div>

                  <div className={styles.productInfo}>
                    <h3 className={styles.productTitle}>{palette.name}</h3>
                    <p className={styles.productDescription}>{palette.description}</p>

                    <div className={styles.productFooter}>
                      <span className={styles.price}>EGP {palette.price}</span>
                      <Button
                        variant="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart({
                            id: palette.id,
                            name: palette.name,
                            price: palette.price,
                            imageUrl: palette.image,
                            type: "palette",
                          });
                          setAddedToCart(palette.id);
                          setTimeout(() => setAddedToCart(null), 2000);
                        }}
                        disabled={!palette.inStock}
                      >
                        {addedToCart === palette.id ? (
                          <>
                            <Check size={18} />
                            Added!
                          </>
                        ) : (
                          <>
                            <ShoppingCart size={18} />
                            Add to Cart
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && palettes.length === 0 && (
            <div className={styles.noResults}>
              <p>No color palettes available right now.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
